/**
 * G9 feedback UI: project Settings › Feedback sources (connect, test, trust tiers, autonomy) and the
 * Feedback review queue (drillable, batch approve / merge / reject).
 *
 * Everything shown from a report is untrusted external content: rendered as quoted text (React
 * escapes it), labelled "untrusted", links shown with their full URL and opened without referrer.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  FEEDBACK_TIER_LABEL,
  type FeedbackConfigRecord,
  type FeedbackFlag,
  type FeedbackItem,
  FeedbackSource,
  type FeedbackSourceKind,
  type FeedbackSourceStatus,
  type FeedbackTier,
} from '@viko/protocol'
import { ChevronDown, ChevronRight, Lock, ShieldAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import {
  decideFeedback,
  type FeedbackAction,
  type FeedbackEntityStore,
  type FeedbackState,
  loadFeedback,
  type ProposalView,
  parseMatchers,
  removeSource,
  requestTest,
  saveFeedbackConfig,
  upsertSource,
} from '../lib/feedback.js'
import { useMachines } from '../lib/queries.js'
import { activePoll } from '../lib/query-client.js'
import type { Project } from '../lib/types.js'
import {
  Ago,
  Badge,
  Button,
  Empty,
  errorText,
  Field,
  Input,
  Select,
  Textarea,
  useToast,
} from './ui.js'

const KIND_LABEL: Record<FeedbackSourceKind, string> = {
  email: 'Email (IMAP)',
  testflight: 'TestFlight',
  crash: 'Crash reports (Sentry)',
  custom: 'Custom endpoint',
}
const FLAG_LABEL: Record<FeedbackFlag, string> = {
  'identity-unverified': 'Sender identity not verified',
  'injection-suspect': 'Contains instruction-like text',
  truncated: 'Truncated',
  'links-removed': 'Unsafe links removed',
  'attachments-withheld': 'Attachments withheld',
  malformed: 'Malformed input',
}
const TIER_TONE: Record<FeedbackTier, 'ok' | 'accent' | undefined> = {
  dev: 'ok',
  tester: 'accent',
  public: undefined,
}

const feedbackKey = (projectId: string) => ['feedback', projectId] as const

function useFeedback(projectId: string) {
  const b = useBackend()
  const store = b.entities as unknown as FeedbackEntityStore | undefined
  const q = useQuery({
    queryKey: feedbackKey(projectId),
    queryFn: () => loadFeedback(store as FeedbackEntityStore, projectId),
    enabled: !!store,
    ...activePoll(60_000),
  })
  return { store, q }
}

function Locked() {
  return (
    <p role="note" className="m-0 rounded-xl bg-surface-2 p-3 text-[14px] text-text-2">
      Feedback needs an unlocked encrypted account: sources, reports and decisions are end-to-end
      encrypted and only readable on your devices and machines.
    </p>
  )
}

// ---------------------------------------------------------------------------------------------
// Settings › Feedback sources
// ---------------------------------------------------------------------------------------------

function statusLine(st: FeedbackSourceStatus | undefined) {
  if (!st?.lastPollAt) return 'Not checked yet'
  return st.lastError
    ? 'Last check failed'
    : `${st.ingested} report${st.ingested === 1 ? '' : 's'} imported`
}

type Draft = Record<string, string>
const DEFAULT_TIER: Record<FeedbackSourceKind, FeedbackTier> = {
  email: 'public',
  testflight: 'tester',
  crash: 'public',
  custom: 'public',
}

function draftToSource(kind: FeedbackSourceKind, d: Draft, id: string) {
  const common = {
    id,
    name: d.name?.trim() || KIND_LABEL[kind],
    enabled: true,
    machineId: d.machineId ?? '',
    maxTier: (d.maxTier as FeedbackTier) || DEFAULT_TIER[kind],
    pollMinutes: Number(d.pollMinutes) || 15,
  }
  const opt = (k: string) => (d[k]?.trim() ? { [k]: d[k]?.trim() } : {})
  const settings =
    kind === 'email'
      ? {
          kind,
          host: d.host?.trim(),
          port: Number(d.port) || 993,
          username: d.username?.trim(),
          password: d.password?.trim(),
          mailbox: d.mailbox?.trim() || 'INBOX',
          ...opt('authservId'),
        }
      : kind === 'testflight'
        ? {
            kind,
            appId: d.appId?.trim(),
            keyId: d.keyId?.trim(),
            issuerId: d.issuerId?.trim(),
            privateKey: d.privateKey?.trim(),
          }
        : kind === 'crash'
          ? {
              kind,
              provider: 'sentry',
              baseUrl: d.baseUrl?.trim() || 'https://sentry.io',
              org: d.org?.trim(),
              project: d.project?.trim(),
              token: d.token?.trim(),
            }
          : {
              kind,
              url: d.url?.trim(),
              ...opt('token'),
              ...opt('webhookSecret'),
              reportersAuthenticated: d.reportersAuthenticated === 'yes',
            }
  return FeedbackSource.safeParse({ ...common, settings })
}

const FIELDS: Record<FeedbackSourceKind, [key: string, label: string, hint?: string][]> = {
  email: [
    ['host', 'IMAP host', 'e.g. imap.gmail.com (TLS, port 993)'],
    ['username', 'Username'],
    [
      'password',
      'App password (1Password reference)',
      'op://vault/item/field — resolved only on the machine',
    ],
    ['mailbox', 'Mailbox', 'Default INBOX. Messages are read without marking them seen.'],
    [
      'authservId',
      'Your mail server’s authserv-id',
      'e.g. mx.google.com. Without it no sender counts as verified.',
    ],
  ],
  testflight: [
    ['appId', 'App Store Connect app id'],
    ['keyId', 'API key id'],
    ['issuerId', 'Issuer id'],
    ['privateKey', 'API key .p8 (1Password reference)', 'op://vault/item/field'],
  ],
  crash: [
    ['org', 'Sentry organization'],
    ['project', 'Sentry project'],
    ['token', 'Auth token (1Password reference)', 'op://vault/item/field'],
    ['baseUrl', 'Sentry URL', 'Default https://sentry.io'],
  ],
  custom: [
    ['url', 'Endpoint URL', 'https:// — polled as GET url?since=<cursor>, JSON {items, cursor}'],
    ['token', 'Bearer token (1Password reference, optional)', 'op://vault/item/field'],
    [
      'webhookSecret',
      'Webhook signing secret (1Password reference, optional)',
      'HMAC-SHA256 over "<timestamp>.<body>"',
    ],
  ],
}

function AddSource({ onAdd }: { onAdd: (s: FeedbackSource) => Promise<void> }) {
  const machines = useMachines()
  const [kind, setKind] = useState<FeedbackSourceKind>('testflight')
  const [d, setD] = useState<Draft>({})
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const set = (k: string) => (e: { target: { value: string } }) =>
    setD((p) => ({ ...p, [k]: e.target.value }))
  const machineId = d.machineId ?? machines.data?.[0]?.id ?? ''
  const submit = async () => {
    const id = `${
      (d.name ?? kind)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 30) || kind
    }-${Math.random().toString(36).slice(2, 6)}`
    const parsed = draftToSource(kind, { ...d, machineId }, id)
    if (!parsed.success) {
      const first = parsed.error.issues[0]
      setError(`${String(first?.path.at(-1) ?? 'Source')}: ${first?.message ?? 'invalid'}`)
      return
    }
    setError(undefined)
    setBusy(true)
    try {
      await onAdd(parsed.data)
      setD({})
    } finally {
      setBusy(false)
    }
  }
  return (
    <details className="rounded-xl border border-border-subtle p-3">
      <summary className="cursor-pointer text-[14px] font-medium">Add feedback source</summary>
      <div className="mt-3 grid gap-3">
        <Field label="Source type">
          <Select value={kind} onChange={(e) => setKind(e.target.value as FeedbackSourceKind)}>
            {(Object.keys(KIND_LABEL) as FeedbackSourceKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Name">
          <Input value={d.name ?? ''} onChange={set('name')} placeholder={KIND_LABEL[kind]} />
        </Field>
        <Field
          label="Machine that polls it"
          hint="Credentials are resolved from 1Password on this machine only."
        >
          <Select value={machineId} onChange={set('machineId')}>
            {(machines.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.online ? '' : ' (offline)'}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Highest trust a report from here can get"
          hint="Sender identity must also be verified by the source; unverified senders are always Public."
        >
          <Select value={d.maxTier ?? DEFAULT_TIER[kind]} onChange={set('maxTier')}>
            {(['dev', 'tester', 'public'] as const).map((t) => (
              <option key={t} value={t}>
                {FEEDBACK_TIER_LABEL[t]}
              </option>
            ))}
          </Select>
        </Field>
        {FIELDS[kind].map(([k, label, hint]) => (
          <Field key={k} label={label} hint={hint}>
            <Input value={d[k] ?? ''} onChange={set(k)} autoComplete="off" spellCheck={false} />
          </Field>
        ))}
        {kind === 'custom' ? (
          <label className="flex items-center gap-2 text-[14px]">
            <input
              type="checkbox"
              checked={d.reportersAuthenticated === 'yes'}
              onChange={(e) =>
                setD((p) => ({ ...p, reportersAuthenticated: e.target.checked ? 'yes' : '' }))
              }
            />
            This system signs in its reporters (their ids can map to trust tiers)
          </label>
        ) : null}
        {error ? (
          <p role="alert" className="m-0 text-[14px] text-status-fail">
            {error}
          </p>
        ) : null}
        <div>
          <Button variant="primary" onClick={() => void submit()} disabled={busy || !machineId}>
            Add source
          </Button>
        </div>
      </div>
    </details>
  )
}

function TrustEditor({
  cfg,
  save,
}: {
  cfg: FeedbackConfigRecord
  save: (m: (c: FeedbackConfigRecord) => FeedbackConfigRecord) => Promise<void>
}) {
  const [dev, setDev] = useState(cfg.trust.dev.join('\n'))
  const [tester, setTester] = useState(cfg.trust.tester.join('\n'))
  const parsedDev = parseMatchers(dev)
  const parsedTester = parseMatchers(tester)
  const bad = [...parsedDev.bad, ...parsedTester.bad]
  return (
    <div className="grid gap-3">
      <Field label="Dev team" hint="One email, @domain or reporter id per line. Highest trust.">
        <Textarea
          rows={3}
          value={dev}
          onChange={(e) => setDev(e.target.value)}
          spellCheck={false}
        />
      </Field>
      <Field label="Trusted testers" hint="One email, @domain or reporter id per line.">
        <Textarea
          rows={3}
          value={tester}
          onChange={(e) => setTester(e.target.value)}
          spellCheck={false}
        />
      </Field>
      {bad.length ? (
        <p role="alert" className="m-0 text-[14px] text-status-fail">
          Not a valid email, @domain or id: {bad.join(', ')}
        </p>
      ) : null}
      <div>
        <Button
          disabled={!!bad.length}
          onClick={() =>
            void save((c) => ({ ...c, trust: { dev: parsedDev.ok, tester: parsedTester.ok } }))
          }
        >
          Save trust tiers
        </Button>
      </div>
      <ul className="m-0 grid list-none gap-1 p-0" aria-label="Feedback autonomy per tier">
        {(['dev', 'tester'] as const).map((t) => (
          <li key={t} className="flex items-center gap-2 text-[14px]">
            <span className="flex-1">{FEEDBACK_TIER_LABEL[t]} feedback</span>
            <select
              className="input !min-h-8 !w-auto !py-0.5 text-[14px]"
              aria-label={`${FEEDBACK_TIER_LABEL[t]} feedback`}
              value={cfg.autonomy[t]}
              onChange={(e) =>
                void save((c) => ({
                  ...c,
                  autonomy: { ...c.autonomy, [t]: e.target.value as 'auto' | 'review' },
                }))
              }
            >
              <option value="review">Ask me first</option>
              <option value="auto">Start on its own</option>
            </select>
          </li>
        ))}
        <li className="flex items-center gap-2 text-[14px]">
          <span className="flex-1">Public feedback</span>
          <span className="flex items-center gap-1 text-[14px] text-text-3">
            <Lock size={12} aria-hidden="true" /> Always asks
          </span>
        </li>
      </ul>
      <p className="m-0 text-[14px] text-text-3">
        Reports with instruction-like text or an unverified sender always ask, whatever the tier.
      </p>
    </div>
  )
}

export function FeedbackSettings({ project }: { project: Project }) {
  const { store, q } = useFeedback(project.id)
  const qc = useQueryClient()
  const toast = useToast()
  const machines = useMachines()
  if (!store) return <Locked />
  const state = q.data
  const save = async (mutate: (c: FeedbackConfigRecord) => FeedbackConfigRecord, done?: string) => {
    try {
      await saveFeedbackConfig(store, project.id, state?.configId, mutate)
      if (done) toast(done)
      await qc.invalidateQueries({ queryKey: feedbackKey(project.id) })
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  const machineName = (id: string) => machines.data?.find((m) => m.id === id)?.name ?? id
  const statusOf = (s: FeedbackSource) =>
    state?.status.find((r) => r.machineId === s.machineId)?.sources[s.id]
  return (
    <section className="grid gap-3" aria-labelledby="ps-feedback">
      <h3 id="ps-feedback" className="m-0 text-[15px] font-semibold">
        Feedback sources
      </h3>
      <p className="m-0 text-[14px] text-text-2">
        Reports become task proposals in the Feedback tab. External content is treated as untrusted
        data: it never grants tools or permissions, and public feedback never starts work without
        you.
      </p>
      {q.isPending ? null : (
        <ul className="m-0 grid list-none gap-2 p-0" aria-label="Connected feedback sources">
          {(state?.config.sources ?? []).map((s) => {
            const st = statusOf(s)
            const requested = state?.config.testRequests[s.id]
            return (
              <li key={s.id} className="card grid gap-2 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[14px] font-medium">{s.name}</span>
                  <Badge>{KIND_LABEL[s.settings.kind]}</Badge>
                  <Badge tone={TIER_TONE[s.maxTier]}>Up to {FEEDBACK_TIER_LABEL[s.maxTier]}</Badge>
                  {s.enabled ? null : <Badge tone="wait">Paused</Badge>}
                </div>
                <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[14px] text-text-2">
                  <dt>Polled by</dt>
                  <dd className="m-0">
                    {machineName(s.machineId)} every {s.pollMinutes} min
                  </dd>
                  <dt>Status</dt>
                  <dd className="m-0">
                    {statusLine(st)}
                    {st?.lastPollAt ? (
                      <>
                        {' · '}
                        <Ago ts={st.lastPollAt} />
                      </>
                    ) : null}
                  </dd>
                  {st?.lastError ? (
                    <>
                      <dt>Error</dt>
                      <dd className="m-0 text-status-fail">{st.lastError}</dd>
                    </>
                  ) : null}
                  {requested ? (
                    <>
                      <dt>Test</dt>
                      <dd className="m-0" aria-live="polite">
                        {st?.test && st.test.requestedAt >= requested
                          ? `${st.test.ok ? 'OK' : 'Failed'}: ${st.test.detail}`
                          : `Waiting for ${machineName(s.machineId)}…`}
                      </dd>
                    </>
                  ) : null}
                </dl>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => void save((c) => requestTest(c, s.id), 'Test requested')}
                  >
                    Test
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => void save((c) => upsertSource(c, { ...s, enabled: !s.enabled }))}
                  >
                    {s.enabled ? 'Pause' : 'Resume'}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => void save((c) => removeSource(c, s.id), 'Removed')}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      <AddSource onAdd={(s) => save((c) => upsertSource(c, s), 'Source added')} />
      <h4 className="m-0 text-[14px] font-semibold">Trust tiers</h4>
      {state ? (
        <TrustEditor
          key={state.configId ?? 'new'}
          cfg={state.config}
          save={(m) => save(m, 'Saved')}
        />
      ) : null}
    </section>
  )
}

// ---------------------------------------------------------------------------------------------
// Feedback review queue
// ---------------------------------------------------------------------------------------------

function UntrustedItem({ item }: { item: FeedbackItem }) {
  return (
    <figure className="m-0 grid gap-1.5 rounded-xl border border-border-subtle p-3">
      <figcaption className="flex flex-wrap items-center gap-2 text-[14px] text-text-2">
        <span className="font-medium text-text-1">{item.sender.display}</span>
        {item.sender.address && item.sender.address !== item.sender.display ? (
          <span>&lt;{item.sender.address}&gt;</span>
        ) : null}
        <Badge tone={TIER_TONE[item.tier]}>{FEEDBACK_TIER_LABEL[item.tier]}</Badge>
        <Badge tone={item.sender.verified ? 'ok' : 'wait'}>
          {item.sender.verified ? 'Verified sender' : 'Unverified sender'}
        </Badge>
        <span>
          {item.sourceName} · <Ago ts={item.receivedAt} />
        </span>
      </figcaption>
      {item.subject ? <p className="m-0 text-[14px] font-medium">“{item.subject}”</p> : null}
      <blockquote
        className="m-0 whitespace-pre-wrap break-words border-l-2 border-border-subtle pl-3 text-[14px] text-text-1"
        aria-label="Untrusted report text"
      >
        {item.body || '(empty)'}
      </blockquote>
      {item.links.length ? (
        <ul className="m-0 grid list-none gap-0.5 p-0 text-[14px]" aria-label="Links in the report">
          {item.links.map((l, n) => (
            <li key={l.url} className="break-all">
              [link {n + 1}: <strong>{l.host}</strong>]{' '}
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                referrerPolicy="no-referrer"
              >
                {l.url}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {item.attachments.length ? (
        <p className="m-0 text-[14px] text-text-2">
          Attachments not downloaded:{' '}
          {item.attachments
            .map((a) => `${a.name} (${a.type}, ${a.bytes.toLocaleString()} bytes)`)
            .join(', ')}
        </p>
      ) : null}
      {Object.keys(item.meta).length ? (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 text-[14px] text-text-2">
          {Object.entries(item.meta).map(([k, v]) => (
            <div key={k} className="contents">
              <dt>{k}</dt>
              <dd className="m-0 break-all">{v}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {item.flags.length ? (
        <p className="m-0 flex flex-wrap gap-1">
          {item.flags.map((f) => (
            <Badge
              key={f}
              tone={f === 'injection-suspect' || f === 'identity-unverified' ? 'fail' : 'wait'}
            >
              {FLAG_LABEL[f]}
            </Badge>
          ))}
        </p>
      ) : null}
    </figure>
  )
}

const STATUS_LABEL = {
  pending: 'Needs review',
  approved: 'Approved',
  rejected: 'Rejected',
  merged: 'Merged',
}

function ProposalRow({
  v,
  checked,
  onCheck,
}: {
  v: ProposalView
  checked: boolean
  onCheck: (on: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const p = v.p
  const flags = [...new Set(p.items.flatMap((i) => i.flags))]
  const sources = [...new Set(p.items.map((i) => i.sourceName))]
  const taskId = v.decision?.taskId ?? p.taskId
  return (
    <li className="card grid gap-2 p-3">
      <div className="flex items-start gap-2">
        {v.status === 'pending' ? (
          <input
            type="checkbox"
            className="mt-1"
            checked={checked}
            onChange={(e) => onCheck(e.target.checked)}
            aria-label={`Select “${p.title}”`}
          />
        ) : null}
        <button
          type="button"
          className="grid flex-1 gap-1 text-left"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span className="flex flex-wrap items-center gap-2 text-[14px]">
            {open ? (
              <ChevronDown size={14} aria-hidden="true" />
            ) : (
              <ChevronRight size={14} aria-hidden="true" />
            )}
            <span className="font-medium">“{p.title || 'Untitled report'}”</span>
            <span className="text-text-3">(untrusted text)</span>
          </span>
          <span className="flex flex-wrap items-center gap-2 text-[14px] text-text-2">
            <Badge
              tone={v.status === 'pending' ? 'wait' : v.status === 'approved' ? 'ok' : undefined}
            >
              {STATUS_LABEL[v.status]}
            </Badge>
            <Badge tone={TIER_TONE[p.tier]}>{FEEDBACK_TIER_LABEL[p.tier]}</Badge>
            <span>
              {p.count.toLocaleString()} report{p.count === 1 ? '' : 's'} · priority {p.priority} ·{' '}
              {sources.join(', ')} · last <Ago ts={p.lastAt} />
            </span>
            {flags.includes('injection-suspect') ? (
              <span className="flex items-center gap-1 text-status-fail">
                <ShieldAlert size={14} aria-hidden="true" /> Instruction-like text
              </span>
            ) : null}
          </span>
        </button>
      </div>
      {open ? (
        <div className="grid gap-2">
          <p className="m-0 text-[14px] text-text-2">
            {v.status === 'pending'
              ? p.reviewReason
              : `Decided${p.decidedBy === 'autonomy' ? ' by project autonomy' : ' by you'}.`}
            {p.topTier !== p.tier ? ` Includes ${FEEDBACK_TIER_LABEL[p.topTier]} reports.` : ''}
            {taskId ? (
              <>
                {' '}
                <Link to="/t/$taskId" params={{ taskId }} className="text-accent-text">
                  Open task
                </Link>
              </>
            ) : null}
          </p>
          {p.summary ? (
            <blockquote className="m-0 whitespace-pre-wrap border-l-2 border-border-subtle pl-3 text-[14px]">
              <span className="block text-text-3">
                Agent summary of untrusted reports (also untrusted)
              </span>
              {p.summary}
            </blockquote>
          ) : null}
          {p.items.map((i) => (
            <UntrustedItem key={i.id} item={i} />
          ))}
          {p.count > p.items.length ? (
            <p className="m-0 text-[14px] text-text-3">
              {(p.count - p.items.length).toLocaleString()} more duplicate report
              {p.count - p.items.length === 1 ? '' : 's'} kept on the polling machine.
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

type Filter = 'pending' | 'decided' | 'all'

export function FeedbackQueue({ project }: { project: Project }) {
  const { store, q } = useFeedback(project.id)
  const qc = useQueryClient()
  const toast = useToast()
  const [filter, setFilter] = useState<Filter>('pending')
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const rows = q.data?.proposals ?? []
  const counts = useMemo(
    () => ({
      pending: rows.filter((r) => r.status === 'pending').length,
      decided: rows.filter((r) => r.status !== 'pending').length,
      all: rows.length,
    }),
    [rows],
  )
  if (!store) return <Locked />
  if (q.isPending) return null
  if (q.isError) return <Empty title="Feedback could not be read">{errorText(q.error)}</Empty>
  const shown = rows.filter((r) =>
    filter === 'all'
      ? true
      : filter === 'pending'
        ? r.status === 'pending'
        : r.status !== 'pending',
  )
  const chosen = rows.filter((r) => sel.has(r.id))
  const act = async (action: FeedbackAction) => {
    setBusy(true)
    try {
      const r = await decideFeedback(store, project.id, chosen, action)
      toast(
        `${r.decided.length} ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'merged'}${r.skipped.length && action !== 'merge' ? ` · ${r.skipped.length} already decided` : ''}`,
      )
      setSel(new Set())
      await qc.invalidateQueries({ queryKey: feedbackKey(project.id) })
      await qc.invalidateQueries({ queryKey: ['tasks'] })
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="grid gap-3 p-4" aria-label="Feedback review queue">
      <div className="choice" role="radiogroup" aria-label="Show">
        {(
          [
            ['pending', 'Needs review'],
            ['decided', 'Decided'],
            ['all', 'All'],
          ] as const
        ).map(([f, label]) => (
          <button
            key={f}
            type="button"
            role="radio"
            aria-checked={filter === f}
            onClick={() => setFilter(f)}
          >
            {label} ({counts[f].toLocaleString()})
          </button>
        ))}
      </div>
      {chosen.length ? (
        <div
          className="flex flex-wrap items-center gap-2"
          role="toolbar"
          aria-label="Batch actions"
        >
          <span className="text-[14px] text-text-2">{chosen.length} selected</span>
          <Button size="sm" variant="primary" disabled={busy} onClick={() => void act('approve')}>
            Approve {chosen.length}
          </Button>
          <Button size="sm" disabled={busy || chosen.length < 2} onClick={() => void act('merge')}>
            Merge {chosen.length}
          </Button>
          <Button size="sm" variant="danger" disabled={busy} onClick={() => void act('reject')}>
            Reject {chosen.length}
          </Button>
        </div>
      ) : null}
      {shown.length ? (
        <ul className="m-0 grid list-none gap-2 p-0" aria-label="Feedback proposals">
          {shown.map((v) => (
            <ProposalRow
              key={v.id}
              v={v}
              checked={sel.has(v.id)}
              onCheck={(on) =>
                setSel((prev) => {
                  const next = new Set(prev)
                  if (on) next.add(v.id)
                  else next.delete(v.id)
                  return next
                })
              }
            />
          ))}
        </ul>
      ) : (
        <Empty title={filter === 'pending' ? 'Nothing to review' : 'No feedback yet'}>
          Connect TestFlight, email, crash reports or your own endpoint in Settings › Feedback
          sources.
        </Empty>
      )}
    </section>
  )
}

export type { FeedbackState }
